import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { DrillCone, DrillSpec, DrillEntity } from '@4cm/core/drills/types';
import { supabase } from '../lib/supabase';
import { DrillPitchSvg } from '../components/drillCanvas/DrillPitchSvg';
import { pitchSpecForAgeGroup } from '../components/drillCanvas/pitchSpecs';
import { pitchAspect } from '../components/drillCanvas/pitchArea';
import { TacticsBoard3D } from '../components/tactics3d/TacticsBoard3D';
import { Drill3DLayer } from '../components/drillCanvas/Drill3DLayer';
import { EquipmentOverlay2D } from '../components/drillCanvas/EquipmentOverlay2D';
import { DrillToolRail } from '../components/drillCanvas/DrillToolRail';
import { DrawingOverlay2D } from '../components/drillCanvas/DrawingOverlay2D';
import type { DrawingMode } from '../components/drillCanvas/drawingTypes';
import type { DrillArrow } from '@4cm/core/drills/types';
import type { EquipmentVariant } from '../components/drillCanvas/equipmentTypes';
import { RosterPickerSheet } from '../components/drillCanvas/RosterPickerSheet';
import type { RosterPlayer } from '../components/drillCanvas/RosterPickerSheet';
import { DrillFormPanel } from '../components/drillCanvas/DrillFormPanel';
import { DrillBrainPanel } from '../components/drillCanvas/DrillBrainPanel';
import { LibrarySheet } from '../components/drillCanvas/LibrarySheet';
import type { DrillTemplate } from '@4cm/core/drills/library';
import { NotesAndClipPanel } from '../components/drillCanvas/NotesAndClipPanel';
import { DrillAnimationBar } from '../components/drillCanvas/DrillAnimationBar';
import { useDrillPlayback, captureKeyframe } from '../lib/drillPlayback';
import type { DrillKeyframe } from '@4cm/core/drills/types';
import { MatchExtractSheet } from '../components/drillCanvas/MatchExtractSheet';
import type { VideoTagRow } from '../components/drillCanvas/MatchExtractSheet';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useAuth } from '../contexts/AuthContext';
import { glassButton, glassButtonActive } from '../components/drillCanvas/liquidGlass';
import { PitchSizePicker } from '../components/drillCanvas/PitchSizePicker';
import { PitchAreaOverlay } from '../components/drillCanvas/PitchAreaOverlay';
import type { PitchView, CustomPitch } from '../components/drillCanvas/pitchArea';
import { PlayerRoleOverlay2D } from '../components/drillCanvas/PlayerRoleOverlay2D';
import { ZonesTextsOverlay2D } from '../components/drillCanvas/ZonesTextsOverlay2D';
import type { DrillRolePlayer } from '../components/drillCanvas/playerRoles';
import {
  entitiesToRolePlayers,
  resolveArrowAnchors,
  liftLegacyKeyframes,
} from '../components/drillCanvas/legacyDrillSpec';
import {
  loadTacticsLayout,
  saveTacticsLayout,
} from '../lib/tactics3dStore';
import {
  drillSpecToTacticsLayout,
  tacticsLayoutToDrillSpec,
} from '../lib/drillTacticsAdapter';
import { GlassPill } from '../components/primitives';
import './SessionTactics3D.css';

interface DrillRow {
  id: string;
  name: string | null;
  drill_spec: DrillSpec | null;
}

/**
 * /drills/:id/canvas — drill authoring on Josh's tactics canvas.
 *
 * Mounts TacticsBoard2D or TacticsBoard3D with a drill-scoped persistKey
 * (`tactics-3d-drill-${drillId}`) so the same canvas serves both purposes
 * without bleeding edits between drills and sessions. On first mount, if
 * the drill has a drill_spec we hydrate the layout into localStorage; the
 * board picks it up via its own load on mount. Save converts the live
 * layout back to drill_spec and writes to drills.drill_spec.
 *
 * Brain integration: TacticsBrainPanel is mounted alongside. Brain can
 * draft tactics patterns / sequences / annotations which apply to the
 * same persistKey, so AI assistance works in the drill context.
 */
export function DrillCanvasPage(): React.JSX.Element {
  const { id: drillId } = useParams<{ id: string }>();
  const { team } = useActiveTeam();
  const { user } = useAuth();
  const [drill, setDrill] = useState<DrillRow | null>(null);
  const [view, setView] = useState<'2d' | '3d'>('2d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  // Equipment lives in drill_spec.cones, edited directly here so the
  // tray overlay doesn't have to round-trip through TacticsLayout
  // (which has no equipment surface).
  const [cones, setCones] = useState<DrillCone[]>([]);
  const [activeTool, setActiveTool] = useState<EquipmentVariant | null>(null);
  const [pitchView, setPitchView] = useState<PitchView>('full');
  const [customPitch, setCustomPitch] = useState<CustomPitch | null>(null);
  // Role-based players (Attacker / Defender / Neutral / Bumper /
  // Server / Coach / Goalkeeper). Stored in drill_spec under a custom
  // `rolePlayers` key alongside cones.
  const [rolePlayers, setRolePlayers] = useState<DrillRolePlayer[]>([]);
  const [targetPlayerCount, setTargetPlayerCount] = useState(0);
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);
  // Drawing tools (Wave: drill canvas unification). Arrows live in
  // drill_spec.arrows; the DrawingOverlay captures click+drag.
  const [arrows, setArrows] = useState<DrillArrow[]>([]);
  // Multi-keyframe animation state. Lives in drill_spec.keyframes and
  // round-trips via the same Save flow as everything else.
  const [keyframes, setKeyframes] = useState<DrillKeyframe[]>([]);

  // Playback hook — interpolates rolePlayers / cones / arrows across
  // keyframes during play / scrub. When idle the overlays render the
  // live coach-edit state.
  const playback = useDrillPlayback({
    keyframes,
    livePlayers: rolePlayers,
    liveCones: cones,
    liveArrows: arrows,
  });

  const handleCaptureKeyframe = useCallback(() => {
    setKeyframes((prev) => {
      const label = `Phase ${prev.length + 1}`;
      const durationSec = prev.length === 0 ? 0 : 1.5;
      return [...prev, captureKeyframe(label, durationSec, rolePlayers, cones, arrows)];
    });
  }, [rolePlayers, cones, arrows]);

  const handleRemoveKeyframe = useCallback((id: string) => {
    setKeyframes((prev) => prev.filter((k) => k.id !== id));
  }, []);

  const handleRenameKeyframe = useCallback((id: string, label: string) => {
    setKeyframes((prev) => prev.map((k) => (k.id === id ? { ...k, label } : k)));
  }, []);

  const handleSetKeyframeDuration = useCallback((id: string, sec: number) => {
    setKeyframes((prev) => prev.map((k) => (k.id === id ? { ...k, durationSec: Math.max(0.1, sec) } : k)));
  }, []);
  const [activeDrawMode, setActiveDrawMode] = useState<DrawingMode | null>(null);
  // Name-confirm modal — opens when the coach saves a still-untitled
  // drill so they can't end up with "New drill" rows cluttering the
  // library.
  const [nameDraftOpen, setNameDraftOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [rosterOpen, setRosterOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [matchExtractOpen, setMatchExtractOpen] = useState(false);

  // Track player_ids the coach has placed during this session so the
  // sheet can flag duplicates. Persists only in component state; the
  // canonical "who's on the pitch" is the TacticsLayout itself.
  const [placedPlayerIds, setPlacedPlayerIds] = useState<Set<string>>(new Set());

  const persistKey = drillId ? `tactics-3d-drill-${drillId}` : null;

  // Pitch spec is derived from the active team's age group. U7-U8 →
  // 5v5 pitch, U9-U10 → 7v7, U11-U12 → 9v9, U13-U17 → 11v11 youth,
  // U18+ → senior. Falls back to 11v11 youth when there's no team.
  const pitchSpec = useMemo(() => pitchSpecForAgeGroup(team?.age_group ?? null), [team?.age_group]);

  // Match-extract pick: seed a generic formation onto the canvas +
  // attach the clip as the drill's reference. v1 doesn't pull tracked
  // positions; the coach drags into final shape.
  const handleMatchExtractPick = useCallback(async (clip: VideoTagRow) => {
    if (!persistKey || !drillId) return;
    const homeSpots = [
      { role: 'GK', x: 0, y: 0.6, z: -45 },
      { role: 'CB', x: -8, y: 0.6, z: -25 },
      { role: 'CB', x: 8, y: 0.6, z: -25 },
      { role: 'CM', x: 0, y: 0.6, z: -5 },
      { role: 'LW', x: -18, y: 0.6, z: 15 },
      { role: 'ST', x: 0, y: 0.6, z: 25 },
    ];
    const awaySpots = [
      { role: 'CB', x: -8, y: 0.6, z: 25 },
      { role: 'CB', x: 8, y: 0.6, z: 25 },
      { role: 'CM', x: 0, y: 0.6, z: 5 },
      { role: 'GK', x: 0, y: 0.6, z: 45 },
    ];
    const layout = loadTacticsLayout(persistKey) ?? {
      homeFormation: '4-3-3',
      awayFormation: '4-3-3',
      homeSpots: [],
      awaySpots: [],
      arrows: [],
    };
    saveTacticsLayout(persistKey, {
      ...layout,
      homeSpots: [...layout.homeSpots, ...homeSpots],
      awaySpots: [...layout.awaySpots, ...awaySpots],
    });
    await supabase
      .from('drills')
      .update({
        reference_clip: {
          url: clip.clip_url,
          ts_in: clip.ts_in,
          ts_out: clip.ts_out,
          note: clip.coach_note ?? clip.notes ?? `Extracted from clip ${clip.id}`,
        },
      })
      .eq('id', drillId);
    setMatchExtractOpen(false);
  }, [persistKey, drillId]);

  // Apply a library template additively: spec.entities → spots,
  // spec.cones → equipment overlay, spec.arrows → tactics arrows. Coach
  // can pick multiple templates and they layer on the same canvas.
  const handleApplyTemplate = useCallback((template: DrillTemplate) => {
    if (!persistKey) return;
    const existing = loadTacticsLayout(persistKey) ?? {
      homeFormation: '4-3-3',
      awayFormation: '4-3-3',
      homeSpots: [],
      awaySpots: [],
      arrows: [],
    };
    const layoutFromTemplate = drillSpecToTacticsLayout(template.spec);
    saveTacticsLayout(persistKey, {
      ...existing,
      homeSpots: [...existing.homeSpots, ...layoutFromTemplate.homeSpots],
      awaySpots: [...existing.awaySpots, ...layoutFromTemplate.awaySpots],
      arrows: [...existing.arrows, ...layoutFromTemplate.arrows],
    });
    if (template.spec.cones && template.spec.cones.length > 0) {
      const stamped = template.spec.cones.map((c) => ({
        ...c,
        id: c.id || `eq-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      }));
      setCones((prev) => [...prev, ...stamped]);
    }
    setLibraryOpen(false);
  }, [persistKey]);

  // Add a roster player to the live TacticsLayout. Lands at pitch
  // centre with a small jitter so successive picks don't stack on the
  // same pixel. The coach drags into final position.
  const handleRosterPick = useCallback((p: RosterPlayer) => {
    if (!persistKey) return;
    const layout = loadTacticsLayout(persistKey) ?? {
      homeFormation: '4-3-3',
      awayFormation: '4-3-3',
      homeSpots: [],
      awaySpots: [],
      arrows: [],
    };
    // Tactics coords are world metres, pitch-centred. Spot at (0,0)
    // is pitch centre. Jitter ±4m in each direction so multi-picks
    // visually fan out.
    const jx = (Math.random() - 0.5) * 8;
    const jz = (Math.random() - 0.5) * 8;
    // Role field carries jersey + position so it shows on the token.
    const roleLabel = p.jerseyNumber != null
      ? `${p.jerseyNumber}${p.position ? ` · ${p.position}` : ''}`
      : (p.position ?? p.name);
    const nextLayout = {
      ...layout,
      homeSpots: [
        ...layout.homeSpots,
        { role: roleLabel, x: jx, y: 0.6, z: jz },
      ],
    };
    saveTacticsLayout(persistKey, nextLayout);
    setPlacedPlayerIds((prev) => {
      const next = new Set(prev);
      next.add(p.playerId);
      return next;
    });
  }, [persistKey]);

  // Load + seed from drill_spec on first mount.
  useEffect(() => {
    if (!drillId) {
      setError('No drill id in URL.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error: err } = await supabase
        .from('drills')
        .select('id, name, drill_spec')
        .eq('id', drillId)
        .maybeSingle();
      if (cancelled) return;
      if (err || !data) {
        setError(err?.message ?? 'Drill not found.');
        setLoading(false);
        return;
      }
      const row = data as DrillRow;
      setDrill(row);
      // Equipment is owned by this page's state — seed from the drill_spec.
      setCones(row.drill_spec?.cones ?? []);
      // Pitch view + custom dims live on drill_spec.
      // Prefer the extended pitchView (handles 'custom' too) if present,
      // else fall back to the canonical DrillSpec.pitchView.
      const specExtrasForView = row.drill_spec as Record<string, unknown> | null;
      const extendedView = specExtrasForView?.pitchViewExtended as PitchView | undefined;
      setPitchView(extendedView ?? (row.drill_spec?.pitchView as PitchView | undefined) ?? 'full');
      if (row.drill_spec?.customPitch) {
        setCustomPitch({
          width: row.drill_spec.customPitch.width,
          length: row.drill_spec.customPitch.length,
          unit: 'm',
        });
      } else {
        setCustomPitch(null);
      }
      // Role-based players + target count live as extras on the spec.
      const specExtras = row.drill_spec as Record<string, unknown> | null;
      const explicitRolePlayers = (specExtras?.rolePlayers as DrillRolePlayer[] | undefined) ?? [];
      const legacyEntities = (row.drill_spec?.entities as DrillEntity[] | undefined) ?? [];
      // Legacy drills (older library drills, and anything the Brain's
      // draft_drill tool authors) store players as `entities` with no
      // `rolePlayers` — the canvas renders players only from rolePlayers, so
      // project them. v2 (canvas-authored) drills already have rolePlayers and
      // pass through unchanged.
      const players = explicitRolePlayers.length > 0
        ? explicitRolePlayers
        : entitiesToRolePlayers(legacyEntities);
      setRolePlayers(players);
      setTargetPlayerCount((specExtras?.targetPlayerCount as number | undefined) ?? 0);
      // DrawingOverlay2D only draws point-anchored arrows (entity anchors
      // collapse to pitch-centre), so resolve entity endpoints to the player
      // positions. No-op for arrows already drawn as points in the canvas.
      const rawArrows = (row.drill_spec?.arrows as DrillArrow[] | undefined) ?? [];
      setArrows(resolveArrowAnchors(rawArrows, players, legacyEntities));
      // Lift v1 (entity-keyed) keyframes into v2 when we projected players, so
      // legacy drill animations play.
      const rawKeyframes = (row.drill_spec?.keyframes as DrillKeyframe[] | undefined) ?? [];
      setKeyframes(explicitRolePlayers.length > 0 ? rawKeyframes : liftLegacyKeyframes(rawKeyframes));
      // Seed the tactics-layout store from drill_spec IF the localStorage
      // entry is empty. If it already exists, the coach was editing —
      // don't trample their work.
      const key = `tactics-3d-drill-${row.id}`;
      const existing = loadTacticsLayout(key);
      if (!existing && row.drill_spec) {
        const layout = drillSpecToTacticsLayout(row.drill_spec);
        saveTacticsLayout(key, layout);
      } else if (!existing && !row.drill_spec) {
        // Blank starter so the board has a layout to render.
        saveTacticsLayout(key, {
          homeFormation: '4-3-3',
          awayFormation: '4-3-3',
          homeSpots: [],
          awaySpots: [],
          arrows: [],
        });
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [drillId]);

  const runSave = useCallback(async (nameOverride?: string) => {
    if (!drill || !persistKey || saving) return;
    setSaving(true);
    setSavedMsg(null);
    try {
      const layout = loadTacticsLayout(persistKey);
      if (!layout) {
        setSavedMsg('Nothing to save yet.');
        setSaving(false);
        return;
      }
      // Merge: keep the player + arrow data converted from TacticsLayout,
      // but override cones with our current overlay state so equipment
      // edits persist.
      const baseForMerge: DrillSpec = {
        ...(drill.drill_spec ?? {
          schemaVersion: 1,
          pitchPresetId: 'full_11v11',
          customPitch: null,
          entities: [],
          cones: [],
          arrows: [],
          zones: [],
          texts: [],
          keyframes: [],
        }),
        cones,
        arrows,
        keyframes,
        // DrillSpec.pitchView is constrained to a smaller union than
        // ours ('custom' isn't in the canonical type). Cast safely —
        // JSONB stores the wider value and the renderer handles it.
        pitchView: (pitchView === 'custom' ? undefined : pitchView) as DrillSpec['pitchView'],
        customPitch: customPitch ? { width: customPitch.width, length: customPitch.length } : null,
      };
      // Attach drill-canvas extras on the spec — these aren't part of
      // the canonical DrillSpec schema but JSONB accepts them.
      (baseForMerge as unknown as Record<string, unknown>).rolePlayers = rolePlayers;
      (baseForMerge as unknown as Record<string, unknown>).targetPlayerCount = targetPlayerCount;
      (baseForMerge as unknown as Record<string, unknown>).pitchViewExtended = pitchView;
      const nextSpec = tacticsLayoutToDrillSpec(layout, baseForMerge);
      const patch: Record<string, unknown> = { drill_spec: nextSpec };
      if (nameOverride && nameOverride.trim()) {
        patch.name = nameOverride.trim();
      }
      const { error: err } = await supabase
        .from('drills')
        .update(patch)
        .eq('id', drill.id);
      setSaving(false);
      if (err) {
        setSavedMsg(`Save failed: ${err.message}`);
        return;
      }
      // Reflect name change locally so the topbar updates immediately.
      if (typeof patch.name === 'string') {
        setDrill({ ...drill, name: patch.name });
      }
      const savedName = (typeof patch.name === 'string' ? patch.name : drill.name) ?? 'drill';
      setSavedMsg(`✓ Saved "${savedName}" — find it in the Drills library.`);
      window.setTimeout(() => setSavedMsg(null), 5000);
    } catch (e) {
      setSaving(false);
      setSavedMsg(`Save error: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }, [drill, persistKey, saving, cones, pitchView, customPitch, rolePlayers, targetPlayerCount, arrows, keyframes]);

  // Top-level Save-drill handler — pops the name modal first when the
  // Open the name modal on EVERY save, prefilled with the current
  // name (which Brain may have generated). Coach hits Enter to confirm
  // or edits before committing — guarantees no drill is saved with a
  // name the coach hasn't seen.
  const handleSave = useCallback(() => {
    if (!drill) return;
    const placeholders = new Set(['', 'New drill', 'Untitled drill']);
    setNameDraft(placeholders.has(drill.name ?? '') ? '' : (drill.name ?? ''));
    setNameDraftOpen(true);
  }, [drill]);

  // Esc cancels the armed tool.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeTool) setActiveTool(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTool]);

  if (loading) {
    return (
      <main style={{ padding: 40, textAlign: 'center', color: '#666', fontFamily: 'system-ui' }}>
        Loading drill canvas…
      </main>
    );
  }
  if (error || !drill || !persistKey) {
    return (
      <main style={{ padding: 40, fontFamily: 'system-ui' }}>
        <h2 style={{ color: '#c0392b' }}>Couldn't open drill canvas</h2>
        <p>{error ?? 'Drill not found.'}</p>
        <Link to="/drills" style={{ color: '#fff', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: 999, textDecoration: 'none', fontWeight: 600 }}>← Back to drills</Link>
      </main>
    );
  }

  return (
    <main className="session-3d" aria-label="Drill canvas">
      <header className="session-3d__topbar">
        <Link
          to="/drills"
          className="session-3d__link"
          style={{ fontWeight: 700, color: '#fff' }}
        >
          ← Back to drills
        </Link>
        <span style={{ opacity: 0.3, padding: '0 4px' }}>·</span>
        <button
          type="button"
          onClick={() => setView('2d')}
          style={view === '2d' ? glassButtonActive : glassButton}
          aria-pressed={view === '2d'}
        >
          2D
        </button>
        <button
          type="button"
          onClick={() => setView('3d')}
          style={view === '3d' ? glassButtonActive : glassButton}
          aria-pressed={view === '3d'}
        >
          3D
        </button>
        <span style={{ flex: 1 }} />
        {/* Age-group pitch label — derived from active team. */}
        <span
          style={{
            fontSize: 11,
            opacity: 0.75,
            padding: '4px 10px',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.05)',
          }}
          title={`Pitch sized for ${pitchSpec.label} — ${Math.round(pitchSpec.length)}×${Math.round(pitchSpec.width)} m`}
        >
          📐 {pitchSpec.label}
        </span>
        <PitchSizePicker
          view={pitchView}
          custom={customPitch}
          onChange={(v, c) => { setPitchView(v); setCustomPitch(c); }}
        />
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          style={glassButton}
          title="Browse the 12 hand-authored drill templates and apply one to the canvas"
        >
          📚 Library
        </button>
        <button
          type="button"
          onClick={() => setNotesOpen(true)}
          style={glassButton}
          title="Notes + reference video clip"
        >
          🗒 Notes
        </button>
        <button
          type="button"
          onClick={() => setMatchExtractOpen(true)}
          style={glassButton}
          title="Seed a drill from a recent tagged clip"
        >
          🎞 From clip
        </button>
        <button
          type="button"
          onClick={async () => {
            if (!drillId) return;
            const url = `${window.location.origin}/drills/${drillId}`;
            try {
              await navigator.clipboard.writeText(url);
              setSavedMsg('✓ Share link copied to clipboard');
              window.setTimeout(() => setSavedMsg(null), 2000);
            } catch {
              prompt('Copy this link:', url);
            }
          }}
          style={glassButton}
          title="Copy a share link to this drill"
        >
          🔗 Share
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          style={glassButton}
          title="Print or save the drill canvas as PDF"
        >
          🖨 Print / PDF
        </button>
        <button
          type="button"
          onClick={() => setFormOpen(true)}
          style={glassButton}
          title="Open the drill details form (name, phase, corners, coaching points, KB overlays)"
        >
          📝 Details
        </button>
        <button
          type="button"
          onClick={() => setRosterOpen(true)}
          disabled={!team?.id}
          style={{
            padding: '6px 14px',
            background: 'transparent',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 13,
            cursor: team?.id ? 'pointer' : 'not-allowed',
            opacity: team?.id ? 1 : 0.5,
          }}
          title={team?.id ? 'Pick a player from the roster to add to the canvas' : 'No active team — pick a team first'}
        >
          📋 Roster
        </button>
        {savedMsg && (
          <span style={{ color: '#27ae60', fontSize: 12, marginRight: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
            {savedMsg}
            {savedMsg.startsWith('✓') && (
              <Link
                to="/drills"
                style={{ color: '#fff', textDecoration: 'underline', fontWeight: 600 }}
              >
                Open library →
              </Link>
            )}
          </span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '6px 14px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: saving ? 'wait' : 'pointer' }}
        >
          {saving ? 'Saving…' : '💾 Save drill'}
        </button>
      </header>

      <section className="session-3d__hero">
        <div>
          <div className="session-3d__eyebrow">Drill canvas · {view.toUpperCase()}</div>
          <h1>{drill.name ?? 'Untitled drill'}</h1>
        </div>
        <div className="session-3d__pills">
          <GlassPill mono>4-3-3</GlassPill>
          <GlassPill mono>Brain available →</GlassPill>
        </div>
      </section>

      <section
        className="session-3d__split"
        aria-label="Drill canvas workspace"
        style={{
          // Override the default 1-2 column grid: drill canvas has
          // 3 lanes (equipment tray | pitch | brain) at wide screens,
          // and collapses to the pitch only at narrow ones.
          gridTemplateColumns: view === '2d' ? '240px minmax(0, 1fr) 360px' : 'minmax(0, 1fr) 360px',
        }}
      >
        {/* LEFT — equipment tray (Wave 1). Selecting a tool arms the
            overlay to capture clicks for placement. */}
        {view === '2d' && (
          <aside style={{ padding: 12, display: 'flex', flexDirection: 'column', overflowY: 'hidden' }}>
            <DrillToolRail
              targetCount={targetPlayerCount}
              onTargetCountChange={setTargetPlayerCount}
              activeRoleId={activeRoleId}
              onActiveRoleChange={setActiveRoleId}
              placed={rolePlayers}
              activeDrawMode={activeDrawMode}
              onActiveDrawModeChange={setActiveDrawMode}
              arrowCount={arrows.length}
              activeEquipmentTool={activeTool}
              onActiveEquipmentToolChange={setActiveTool}
            />
          </aside>
        )}
        <div
          className="session-3d__pitch"
          style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {view === '2d' ? (
            <div
              style={{
                position: 'relative',
                width: '100%',
                maxWidth: 'min(100%, 1200px)',
                aspectRatio: pitchAspect(pitchSpec, pitchView, customPitch),
                maxHeight: '100%',
              }}
            >
              {/* All drill surfaces share one coordinate system — the
                  pitch backdrop + every overlay use the SAME viewBox
                  computed from the active PitchSpec. No anchor portal,
                  no resize observer. */}
              <DrillPitchSvg spec={pitchSpec} view={pitchView} custom={customPitch} />
              <PitchAreaOverlay view={pitchView} custom={customPitch} spec={pitchSpec} />
              {/* Zones behind equipment/players (read-only — matches the preview). */}
              <ZonesTextsOverlay2D
                zones={drill.drill_spec?.zones ?? []}
                texts={drill.drill_spec?.texts ?? []}
                spec={pitchSpec} view={pitchView} custom={customPitch} layer="back"
              />
              <EquipmentOverlay2D
                cones={playback.animated?.cones ?? cones}
                onChange={setCones}
                activeTool={playback.state.mode === 'playing' ? null : activeTool}
                spec={pitchSpec}
                view={pitchView}
                custom={customPitch}
              />
              <DrawingOverlay2D
                arrows={playback.animated?.arrows ?? arrows}
                onChange={setArrows}
                activeMode={playback.state.mode === 'playing' ? null : activeDrawMode}
                spec={pitchSpec}
                view={pitchView}
                custom={customPitch}
              />
              <PlayerRoleOverlay2D
                players={playback.animated?.rolePlayers ?? rolePlayers}
                onChange={setRolePlayers}
                activeRoleId={playback.state.mode === 'playing' ? null : activeRoleId}
                spec={pitchSpec}
                view={pitchView}
                custom={customPitch}
              />
              {/* Texts on top (read-only — matches the preview). */}
              <ZonesTextsOverlay2D
                zones={drill.drill_spec?.zones ?? []}
                texts={drill.drill_spec?.texts ?? []}
                spec={pitchSpec} view={pitchView} custom={customPitch} layer="front"
              />
            </div>
          ) : (
            <TacticsBoard3D
              persistKey={persistKey}
              hideField={pitchView === 'custom'}
              hideHud
            >
              <Drill3DLayer
                rolePlayers={rolePlayers}
                cones={cones}
                arrows={arrows}
                animatedState={playback.animated}
                pitchSpec={pitchSpec}
                pitchView={pitchView}
                customPitch={customPitch}
              />
            </TacticsBoard3D>
          )}
        </div>
        <aside className="session-3d__brain" aria-label="Brain command panel">
          <DrillBrainPanel
            drillId={drill.id}
            teamId={team?.id ?? null}
            persistKey={persistKey}
            currentSpec={drill.drill_spec}
            onApplyCones={(applied) => {
              // REPLACE when Brain is doing a full setup (keyframes
              // are coming through onApplyKeyframes in the same turn).
              // For now: replace if applied is non-empty AND
              // explicitly came from a setup. The simple heuristic: a
              // full setup always provides cones, players, ball, +
              // arrows, so it's safe to replace.
              setCones(applied);
            }}
            onApplyRolePlayers={(applied) => {
              setRolePlayers(applied);
            }}
            onApplyPitchView={(view, custom) => {
              setPitchView(view);
              setCustomPitch(custom);
            }}
            onApplyTargetCount={(n) => setTargetPlayerCount(n)}
            onApplyArrows={(applied) => {
              setArrows(applied);
            }}
            onApplyKeyframes={(applied) => {
              // Replace — Brain's keyframes define the whole animation,
              // not an append.
              setKeyframes(applied);
            }}
          />
        </aside>
      </section>

      {/* Keyframe timeline sits OUTSIDE the split as a full-width row
          below — keeps the pitch at full height + lets coach scrub
          across the entire bottom edge. */}
      <section style={{ padding: '12px 0' }} aria-label="Animation timeline">
        <DrillAnimationBar
          playback={playback}
          keyframes={keyframes}
          onCapture={handleCaptureKeyframe}
          onRemove={handleRemoveKeyframe}
          onRename={handleRenameKeyframe}
          onSetDuration={handleSetKeyframeDuration}
        />
      </section>

      <RosterPickerSheet
        open={rosterOpen}
        onClose={() => setRosterOpen(false)}
        teamId={team?.id ?? null}
        onPick={handleRosterPick}
        alreadyPlacedIds={placedPlayerIds}
      />

      <DrillFormPanel
        open={formOpen}
        onClose={() => setFormOpen(false)}
        drillId={drillId}
        onSaved={(saved) => {
          // Sync drill name in our local state so the topbar reflects it.
          if (drill) setDrill({ ...drill, name: saved.name });
        }}
      />

      <LibrarySheet
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        onPick={handleApplyTemplate}
      />

      {nameDraftOpen && (
        <div
          role="dialog"
          aria-label="Name your drill"
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setNameDraftOpen(false)}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => {
              e.preventDefault();
              const name = nameDraft.trim();
              if (!name) return;
              setNameDraftOpen(false);
              void runSave(name);
            }}
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              background: 'rgba(15, 25, 45, 0.97)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 12,
              padding: 20,
              color: '#fff',
              fontFamily: 'system-ui, sans-serif',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 16 }}>Name your drill</h2>
            <p style={{ margin: 0, fontSize: 12, opacity: 0.7, lineHeight: 1.5 }}>
              Give it a short name so you can find it again in your library. You can rename it later from the Drill Details panel.
            </p>
            <input
              type="text"
              autoFocus
              value={nameDraft}
              placeholder="e.g. 5v3 rondo — 15m grid"
              onChange={(e) => setNameDraft(e.target.value)}
              style={{
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 8,
                fontSize: 14,
              }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setNameDraftOpen(false)}
                style={{
                  padding: '8px 14px',
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 6,
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!nameDraft.trim()}
                style={{
                  padding: '8px 14px',
                  background: nameDraft.trim() ? '#27ae60' : 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: nameDraft.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Save drill
              </button>
            </div>
          </form>
        </div>
      )}

      <MatchExtractSheet
        open={matchExtractOpen}
        onClose={() => setMatchExtractOpen(false)}
        coachId={user?.id ?? null}
        onPick={handleMatchExtractPick}
      />

      <NotesAndClipPanel
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        drillId={drillId}
        onAiDraftOrganisation={() => {
          window.dispatchEvent(new CustomEvent('drill-brain:send', {
            detail: { prompt: 'Draft organisation notes (markdown bullets) for the current drill — how to set it up, area sizes, player numbers, simple rules. 4-6 lines.' },
          }));
        }}
        onAiDraftCoachingPoints={() => {
          window.dispatchEvent(new CustomEvent('drill-brain:send', {
            detail: { prompt: 'Draft coaching points (markdown bullets) for the current drill — what to observe + coach. 3-5 lines.' },
          }));
        }}
      />
    </main>
  );
}
